import { Component, Input, OnInit } from '@angular/core';
import * as _ from 'lodash';
import * as d3 from 'd3';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../../services/core.translate.service';
import { ProjectService } from '../../../../services/project.service';
import { FilterService } from '../../../../services/core.filter.service';
import { UiService } from '../../../../services/core.ui.service';
import { ProjectReportsData } from '../../services/project.reports.data.service';
import { TrackerReportHelper } from './project.report.tracker.service.component';

@Component({
    selector: 'project-tracker-report',
    template: require('./project.report.tracker.component.html')
})
export class ProjectTrackerReportComponent implements OnInit {
    @Input() project: any = null;

    charts: any = false;
    chartOptions: any;
    paging: any;
    baseFilters: any;
    filters: any;
    sort: any;
    sortFields: any;

    constructor(
        private stateService: StateService,
        private gfTranslateService: GfTranslateService,
        private projectService: ProjectService,
        private trackerReportHelper: TrackerReportHelper,
        private filterService: FilterService,
        private uiService: UiService,
        private projectReportsData: ProjectReportsData
    ) {
        this.chartOptions = {
            chart: {
                type: 'pieChart',
                height: 360,
                width: 360,
                x: function (d: any) { return d.key; },
                y: function (d: any) { return d.y; },
                showLabels: false,
                transitionDuration: 200,
                labelThreshold: 0.01,
                noData: this.gfTranslateService.translate("No Data Available"),
                legendPosition: 'bottom',
                showLegend: false,
                tooltips: true,
                valueFormat: d3.format('d'),
                labelsOutside: true
            }
        };
    }

    ngOnInit(): void {
        this.paging = this.filterService.getPagingValues();

        this.trackerReportHelper.getBaseFilters(this.project)
            .then(
                (filters: any) => {
                    this.baseFilters = filters;
                    this.filters = {
                        main_fields: this.filterService.setFilterValues(
                            _.cloneDeep(filters),
                            this.stateService.params
                        )
                    };
                },
                () => {}
            )
            .finally(() => this.refreshData());
    }

    onApplyFilters(): void {
        this.paging.page_num = 1;
        this.applyFilters();
    }

    onResetFilters(): void {
        this.paging.page_num = 1;
        
        // Resets filters
        this.filters = {
            main_fields: this.filterService.setFilterValues(
                _.cloneDeep(this.baseFilters),
                {}
            )
        };
        
        // Creates list of cleared parameters
        const clearedParams = {};
        _.forEach(this.baseFilters, (filter: any) => {
            clearedParams[filter.alias] = '';
        });
        
        this.applyFilters(clearedParams);
    }

    setSort(params: any): void {
        this.sort = {
            sort_field: _.get(params, 'key', 'releaseDate'),
            sort_dir: _.get(params, 'sortDir', 'desc')
        };
        this.applyFilters();
    }

    downloadData(format: string): void {
        const fetchArgs = this.filterService.getFetchArgs({
            project: this.stateService.params.unixName
        });
        this.projectReportsData.runReport(fetchArgs, format);
    }

    onPaging(params: any): void {
        _.assignIn(this.paging, params);
        this.applyFilters();
    }

    refreshData(): void {
        this.charts = false;

        const fetchArgs = this.getParams({
            project: this.stateService.params.unixName,
            type: this.stateService.params.type
        });

        this.projectService.report(fetchArgs)
            .then(
                (response: any) => {
                    this.charts = this.trackerReportHelper.getChartsData(
                        _.get(response, 'items', {})
                    );
                },
                (response: any) => {
                    console.log(response);

                    this.charts = this.trackerReportHelper.getChartsData(
                        _.get(response, 'items', {})
                    );

                    const msg = this.gfTranslateService.translate('There was a problem loading project activity');
                    this.uiService.message.showWarning(msg, 'Data Error');
                }
            );
    }

    private getParams(overrides: any = null): any {
        return Object.assign(
            {},
            this.sort,
            this.paging,
            this.filterService.getAllFilterValues(
                _.get(this, 'filters.main_fields', [])
            ),
            overrides || {}
        );
    }

    private applyFilters(overrides: any = {}): void {
        const params = this.getParams(overrides);

        const opts = { reload: false, location: true, notify: false };
        this.stateService.go('.', params, opts).then(
            () => {
                this.refreshData();
            }
        );
    }
}